import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ModalActions, ModalActionsProps } from "./ModalActions";
import { IconButton } from "../ui/IconButton";
import "./ZwevendPaneel.css";

export interface ZwevendPaneelProps {
  title: string;
  onClose: () => void;
  /** Breedte op een breed scherm; op een smal scherm altijd de volle breedte. */
  width?: string;
  children: React.ReactNode;
  /** Dezelfde knoppenbalk als StandardModal. */
  actions?: ModalActionsProps;
}

/**
 * Zwevend, niet-blokkerend paneel (B4, fixronde 12-10-2026): geen
 * achtergrondlaag, het dossier erachter blijft scrollbaar en klikbaar. Start
 * rechtsonder en is aan de titelbalk te verslepen. Escape sluit (met de
 * focus in het paneel), Ctrl/Cmd+Enter verstuurt het formulier uit
 * `actions.formId`. Alleen voor "Nieuwe taak" - zie MODALS.md; elk ander
 * venster blijft een StandardModal.
 */
export function ZwevendPaneel({
  title,
  onClose,
  width = "720px",
  children,
  actions,
}: ZwevendPaneelProps) {
  const paneelRef = useRef<HTMLDivElement>(null);
  const [positie, setPositie] = useState<{ left: number; top: number } | null>(
    null,
  );
  const greep = useRef<{ dx: number; dy: number } | null>(null);

  // Zoals Modal: het eerste invoerveld krijgt focus.
  useEffect(() => {
    const eerste = paneelRef.current?.querySelector<HTMLElement>(
      "input, select, textarea, button",
    );
    eerste?.focus();
  }, []);

  const beginSleep = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const rect = paneelRef.current?.getBoundingClientRect();
    if (!rect) return;
    greep.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const tijdensSleep = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!greep.current || !paneelRef.current) return;
    const { offsetWidth, offsetHeight } = paneelRef.current;
    const left = Math.max(
      0,
      Math.min(e.clientX - greep.current.dx, window.innerWidth - offsetWidth),
    );
    const top = Math.max(
      0,
      Math.min(e.clientY - greep.current.dy, window.innerHeight - offsetHeight),
    );
    setPositie({ left, top });
  };

  const eindSleep = (e: React.PointerEvent<HTMLDivElement>) => {
    greep.current = null;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && actions?.formId) {
      e.preventDefault();
      const form = document.getElementById(actions.formId);
      if (!(form instanceof HTMLFormElement)) return;
      if (typeof form.requestSubmit === "function") {
        form.requestSubmit();
      } else {
        form.dispatchEvent(
          new Event("submit", { bubbles: true, cancelable: true }),
        );
      }
    }
  };

  return createPortal(
    <div
      ref={paneelRef}
      className="zwevend-paneel"
      role="dialog"
      aria-modal="false"
      aria-labelledby="zwevend-paneel-titel"
      style={
        positie
          ? {
              width,
              left: positie.left,
              top: positie.top,
              right: "auto",
              bottom: "auto",
            }
          : { width }
      }
      onKeyDown={onKeyDown}
    >
      <div
        className="zwevend-paneel__kop"
        onPointerDown={beginSleep}
        onPointerMove={tijdensSleep}
        onPointerUp={eindSleep}
        onPointerCancel={eindSleep}
      >
        <h3 id="zwevend-paneel-titel">{title}</h3>
        <IconButton
          type="button"
          variant="neutral"
          aria-label="Sluiten"
          title="Sluiten"
          onClick={onClose}
          onPointerDown={(e) => e.stopPropagation()}
          style={{ padding: "4px 10px" }}
        >
          ✕
        </IconButton>
      </div>
      <div className="zwevend-paneel__inhoud">
        {children}
        {actions && <ModalActions {...actions} />}
      </div>
    </div>,
    document.body,
  );
}
